import { FeatureTableOptions } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

import { GeckoWeld } from './geckoWelds'

export interface GeckoPipeSeamWeldJSON {
  name: string | undefined
  type: GeckoFeatureModelType.PipeSeamWeld
  feature_geometry: {
    y_start: number
    y_end: number
    x: number
  }
}
export const NewGeckoPipeSeamWeldJSON: GeckoPipeSeamWeldJSON = {
  name: 'New Pipe Seam Weld',
  type: GeckoFeatureModelType.PipeSeamWeld,
  feature_geometry: {
    y_start: 0,
    y_end: 0,
    x: 0,
  },
}

export class GeckoPipeSeamWeld extends GeckoWeld {
  public readonly type = GeckoFeatureModelType.PipeSeamWeld

  private constructor(
    componentID: string,
    weldID: string,
    weldJson: GeckoPipeSeamWeldJSON
  ) {
    const name = parseString(weldJson.name)
    const feature_geometry = weldJson.feature_geometry as Record<string, number>
    super(
      componentID,
      weldID,
      name,
      feature_geometry.x as number,
      feature_geometry.y_start as number,
      feature_geometry.x as number,
      feature_geometry.y_end as number
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJson: Record<string, GeckoPipeSeamWeldJSON>
  ): Map<string, GeckoPipeSeamWeld> {
    return Object.keys(recordsJson).reduce((resultObj, weldID) => {
      resultObj.set(
        weldID,
        new GeckoPipeSeamWeld(componentID, weldID, recordsJson[weldID])
      )
      return resultObj
    }, new Map<string, GeckoPipeSeamWeld>())
  }

  public asJSON() {
    return generatePipeSeamWeld(
      this.name,
      this.feature_geometry.x.start,
      this.feature_geometry.y.start,
      this.feature_geometry.y.end
    )
  }

  public static CreateFromJSON(
    componentID: string,
    weldID: string,
    weldJson: GeckoPipeSeamWeldJSON
  ): GeckoPipeSeamWeld {
    return new GeckoPipeSeamWeld(componentID, weldID, weldJson)
  }
}

export function generatePipeSeamWeld(
  name: string | undefined,
  x: GeckoMeasurement,
  yStart: GeckoMeasurement,
  yEnd: GeckoMeasurement
): GeckoPipeSeamWeldJSON {
  const pipeSeamWeldJSON: GeckoPipeSeamWeldJSON = {
    name: name ?? undefined,
    type: GeckoFeatureModelType.PipeSeamWeld,
    feature_geometry: {
      x: x
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      y_start: yStart
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      y_end: yEnd
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
  }

  return pipeSeamWeldJSON
}

export const PipeSeamWeldJSONFromFormFields = (formFields: FormField[]) => {
  return generatePipeSeamWeld(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters
    )
  )
}

export const PipeSeamWeldFormFields = (weldJSON: GeckoPipeSeamWeldJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Name',
      value: weldJSON.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: weldJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: weldJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: weldJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const PipeSeamWeldFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.PipeSeamWeld,
  title: 'Pipe Seam Welds',
  singularName: 'Pipe Seam Weld',
  fields: ['x_position', 'y_start', 'y_end'],
}
